import { useEffect, useState } from "react";
import { useForm, UseFormRegister } from "react-hook-form";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Share2, X, ClipboardCheck, FileText, Send } from "lucide-react";
import clsx from "clsx";
import { useAuth } from "../../context/AuthContext";
import { InviaReportModal } from "../../components/ui/InviaReportModal";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage, apiClient } from "../../api/client";
import * as sopralluoghiApi from "../../api/sopralluoghi";
import { CondivisioneSopralluogo, Sopralluogo } from "../../api/sopralluoghi";
import * as anagraficaApi from "../../api/anagrafica";
import { CentroAssistenzaOption } from "../../api/anagrafica";
import { IstitutoVigilanzaOption } from "../../api/anagrafica";
import { Table, TableColumn } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { SlideOver } from "../../components/ui/SlideOver";
import {
  RiferimentiOdoo,
  ValoriRiferimentiOdoo,
  RIFERIMENTI_ODOO_VUOTI,
  riferimentiOdooDaRecord,
  payloadRiferimentiOdoo,
  usaPuoGestireRiferimentiOdoo,
} from "../../components/ui/RiferimentiOdoo";
import { CategorizedMediaUploader } from "../../components/ui/MediaUploader";
import { sectionFlatStyle, sectionTileStyle } from "../../utils/sectionColors";
import { ACCEPT_FOTO_VIDEO } from "../../utils/tipiFileCaricati";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";
import { SedeAutocomplete } from "../../components/ui/SedeAutocomplete";
import { provinciaPerElenco, regionePerProvincia } from "../../utils/province";
import { ExportButton } from "../../components/ui/ExportButton";
import { DispositivoAutocomplete } from "../../components/ui/DispositivoAutocomplete";
import { DispositivoMateriale } from "../../components/ui/DispositivoMateriale";
import { MeseSelect } from "../../components/ui/MeseSelect";
import { meseDaUrl, useOrdinamentoUrl, usePannelloRecord, useScriviParametri } from "../../hooks/useStatoUrl";
import { RitardoBanner } from "../../components/ui/RitardoBanner";
import { AssegnatarioSelect } from "../../components/ui/AssegnatarioSelect";
import { useMessaggioListaVuota } from "../../components/ui/ListaVuota";

/**
 * Gli stati di un sopralluogo.
 *
 * Vivono qui e non in un enum del database: `sopralluoghi.stato` e' un
 * `VarChar(50)` e il backend non lo valida, quindi questo elenco **e'** la
 * definizione. Aggiungerne uno costa una riga; toglierne uno lascerebbe in
 * tabella i record che lo portano, e quelli continuerebbero a mostrarlo.
 *
 * «Non idoneo» dice che il posto NON e' adatto: e' un esito, non un lavoro a
 * meta'. Un sopralluogo non idoneo resta legittimamente **senza installazione
 * collegata**, e non va trattato come qualcosa da completare — vedi il colore
 * qui sotto e il commento sul collegamento in `SopralluoghiCollegati`.
 */
const STATI = ["Da revisionare", "Approvato", "Da rifare", "Non idoneo"];
const SOCIETA = ["Evo", "CIT", "Centro Assistenza", "Cliente", "Altro"];
const RISCHIO_OPZIONI = ["nessuna", "1", "2", "più di 2"];
const PROTEZIONE_OPZIONI = ["nessuna", "inferriate", "vetri blindati", "serrande", "altro"];

// Opzioni allineate al modulo di riferimento "Modulo Sopralluogo WinkonCash"
// (campi che nel form cartaceo/Forms sono tendine, prima qui erano testo libero).
const REGIONI = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia Romagna",
  "Friuli Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
  "Trentino Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
];

const PROVINCE = [
  "AG","AL","AN","AO","AQ","AR","AP","AT","AV","BA","BT","BL","BN","BG","BI",
  "BO","BZ","BS","BR","CA","CL","CB","CI","CE","CT","CZ","CH","CO","CS","CR",
  "KR","CN","EN","FM","FE","FI","FG","FC","FR","GE","GO","GR","IM","IS","SP",
  "LT","LE","LC","LI","LO","LU","MC","MN","MS","MT","VS","ME","MI","MO","MB",
  "NA","NO","NU","OG","OT","OR","PD","PA","PR","PV","PG","PU","PE","PC","PI",
  "PT","PN","PZ","PO","RG","RA","RC","RE","RI","RN","Roma","RO","SA","SS",
  "SV","SI","SR","SO","TA","TE","TR","TO","TP","TN","TV","TS","UD","VA","VE",
  "VB","VC","VR","VV","VI","VT",
];

const TIPI_ATTIVITA = [
  "commercio al dettaglio", "grande distribuzione", "farmacia/parafarmacia",
  "commercio all'ingrosso/cash and carry", "distributore di carburante",
  "piccolo dettagliante", "locali per la ristorazione",
  "agenzia immobiliare/viaggi/servizi", "alimentari", "tabaccheria/ricevitoria",
  "Altro",
];

const UBICAZIONE_OPZIONI = [
  "su strada centro cittadino", "su strada in città zona periferica",
  "su strada fuori città zona isolata", "zona industriale",
  "interno parco/centro commerciale", "esterno in parco/centro commerciale",
  "Altro",
];

const INGRESSO_USCITA_OPZIONI = [
  "ingresso/uscita unico fronte strada", "ingresso/uscita unico galleria commerciale",
  "esistono più ingressi/uscite", "Altro",
];

const NUMERO_ACCESSI_OPZIONI = ["1", "2", "3", "più di 3"];

const TIPO_ACCESSO_LOCALE_OPZIONI = [
  "porta blindata", "porta non blindata", "porta rei", "porta automatica", "porta non presente",
];
const MEDIA_CATEGORIE = [
  { value: "locale_cassaforte", label: "Locale cassaforte" },
  { value: "ingressi_uscite", label: "Ingressi/uscite" },
  { value: "area_esterna_negozio", label: "Area esterna negozio" },
  { value: "area_esterna_locale_cassaforte", label: "Area esterna locale cassaforte" },
  // Unica categoria che ammette anche video, non solo foto — il percorso
  // della vigilanza si documenta meglio con un breve video che con scatti
  // singoli.
  { value: "percorso_vigilanza", label: "Percorso vigilanza (foto o video)", accept: ACCEPT_FOTO_VIDEO },
  // Non è uno strumento di disegno interattivo (penna/forme/frecce come nel
  // riferimento) — qui si carica una foto/scansione della piantina già
  // fatta su carta o esportata da altrove. Un vero editor di disegno è un
  // componente a parte, molto più grande di questo.
  { value: "piantina", label: "Piantina (foto o scansione)" },
];

// Percorso a passi invece di uno scroll unico — ispirato al form reale visto
// negli screenshot di riferimento (9 step con pillole di navigazione).
// "Foto e piantina" è un passo esplicito (non nascosto sotto lo scroll)
// proprio perché richiede un record già salvato — meglio dirlo chiaramente
// dentro il percorso che lasciarlo scoprire scorrendo oltre il form.
const STEPS = [
  "Compilatore e cliente",
  "Location",
  "Ispezione locali",
  "Locale cassaforte",
  "Impianto elettrico/rete",
  "Impianto antintrusione",
  "Posizione cassaforte",
  "Quadro di sintesi",
  "Foto e piantina",
];

type FormValues = {
  da_completare: boolean;
  compilatore_nome: string;
  societa_appartenenza: string;
  data_sopralluogo: string;
  ragione_sociale_cliente: string;
  insegna_cliente: string;
  tipo_attivita: string;
  regione: string;
  provincia: string;
  indirizzo: string;
  /// Solo admin/staff possono valorizzare uno di questi due (mai entrambi) —
  /// assegna il sopralluogo precompilato a un Centro Assistenza o a un
  /// Istituto di Vigilanza, che lo troverà pronto da completare/consultare
  /// invece di crearlo da zero.
  centro_assistenza_id: string;
  istituto_vigilanza_id: string;
  /// Fase 3.4: id dell'azienda anagrafica (per assegnare a un suo utente cliente)
  cliente_id: string;
  /// Sede specifica dell'anagrafica (propedeutico alla vista aggregata per sede)
  sede_id: string;
  /// solo display del SedeAutocomplete (non inviato)
  sede_nome: string;
  /// Fase 3.4: utente cliente assegnato a compilare il sopralluogo
  assegnato_utente_id: string;
  ubicazione_negozio: string;
  note_ubicazione: string;
  tipo_locale: string;
  numero_locali: string;
  tipo_ingresso_uscita: string;
  note_locali: string;
  numero_accessi_locale: string;
  tipo_accesso_locale: string;
  installazione_possibile: string;
  note_installazione: string;
  caratteristiche_pareti: string;
  note_pareti: string;
  caratteristiche_pavimento: string;
  accessi_rischio_finestre: string;
  accessi_rischio_lucernai: string;
  accessi_rischio_vetrine: string;
  note_accessi_rischio: string;
  protezioni_finestre: string;
  protezioni_lucernai: string;
  protezioni_vetrine: string;
  note_protezioni: string;
  prese_schuko: boolean;
  prese_tradizionale: boolean;
  prese_punto_rete: boolean;
  numero_prese: string;
  collegamento_internet: string;
  note_elettrico_rete: string;
  allarme_presente: string;
  allarme_marca: string;
  allarme_modello: string;
  allarme_ultima_manutenzione: string;
  allarme_contratto_manutenzione: string;
  sensori_locale_cassaforte: string;
  altri_sistemi_sicurezza: string;
  collegamento_vigilanza: string;
  istituto_vigilanza_nome: string;
  collegamento_forze_ordine: string;
  note_antintrusione: string;
  posizione_ostacoli: string;
  note_ostacoli: string;
  facile_accesso_installatore: string;
  facile_accesso_vigilanza: string;
  confini_posizione: string;
  accesso_locali: string;
  modello_da_installare: string;
  dispositivo_catalogo_id: string;
  validita_installazione: string;
  esito_ispezione_locali: string;
  esito_locale_cassaforte: string;
  esito_impianto_elettrico: string;
  esito_impianto_antintrusione: string;
  esito_posizione_cassaforte: string;
  disegno_locali_effettuato: string;
  stato: string;
  note: string;
  // I cinque riferimenti OdA/OdV dentro il form: `reset()` li riallinea aprendo
  // un altro sopralluogo, che su un form a nove passi conta più che altrove — il
  // riquadro sta al passo 8 e un valore rimasto da prima non si vedrebbe.
} & ValoriRiferimentiOdoo;

const b2s = (v: boolean | null | undefined): string => (v === true ? "true" : v === false ? "false" : "");
const s2b = (v: string): boolean | null => (v === "true" ? true : v === "false" ? false : null);

function emptyFormValues(): FormValues {
  return {
    da_completare: false,
    compilatore_nome: "",
    societa_appartenenza: "",
    data_sopralluogo: new Date().toISOString().slice(0, 10),
    ragione_sociale_cliente: "",
    insegna_cliente: "",
    tipo_attivita: "",
    centro_assistenza_id: "",
    istituto_vigilanza_id: "",
    cliente_id: "",
    sede_id: "",
    sede_nome: "",
    assegnato_utente_id: "",
    regione: "",
    provincia: "",
    indirizzo: "",
    ubicazione_negozio: "",
    note_ubicazione: "",
    tipo_locale: "",
    numero_locali: "",
    tipo_ingresso_uscita: "",
    note_locali: "",
    numero_accessi_locale: "",
    tipo_accesso_locale: "",
    installazione_possibile: "",
    note_installazione: "",
    caratteristiche_pareti: "",
    note_pareti: "",
    caratteristiche_pavimento: "",
    accessi_rischio_finestre: "",
    accessi_rischio_lucernai: "",
    accessi_rischio_vetrine: "",
    note_accessi_rischio: "",
    protezioni_finestre: "",
    protezioni_lucernai: "",
    protezioni_vetrine: "",
    note_protezioni: "",
    prese_schuko: false,
    prese_tradizionale: false,
    prese_punto_rete: false,
    numero_prese: "",
    collegamento_internet: "",
    note_elettrico_rete: "",
    allarme_presente: "",
    allarme_marca: "",
    allarme_modello: "",
    allarme_ultima_manutenzione: "",
    allarme_contratto_manutenzione: "",
    sensori_locale_cassaforte: "",
    altri_sistemi_sicurezza: "",
    collegamento_vigilanza: "",
    istituto_vigilanza_nome: "",
    collegamento_forze_ordine: "",
    note_antintrusione: "",
    posizione_ostacoli: "",
    note_ostacoli: "",
    facile_accesso_installatore: "",
    facile_accesso_vigilanza: "",
    confini_posizione: "",
    accesso_locali: "",
    modello_da_installare: "",
    dispositivo_catalogo_id: "",
    validita_installazione: "",
    esito_ispezione_locali: "",
    esito_locale_cassaforte: "",
    esito_impianto_elettrico: "",
    esito_impianto_antintrusione: "",
    esito_posizione_cassaforte: "",
    disegno_locali_effettuato: "",
    stato: "Da revisionare",
    note: "",
    ...RIFERIMENTI_ODOO_VUOTI,
  };
}

function toFormValues(r: Sopralluogo): FormValues {
  return {
    da_completare: r.da_completare ?? false,
    compilatore_nome: r.compilatore_nome ?? "",
    societa_appartenenza: r.societa_appartenenza ?? "",
    data_sopralluogo: r.data_sopralluogo?.slice(0, 10) ?? "",
    ragione_sociale_cliente: r.ragione_sociale_cliente ?? "",
    insegna_cliente: r.insegna_cliente ?? "",
    tipo_attivita: r.tipo_attivita ?? "",
    centro_assistenza_id: r.centro_assistenza_id ?? "",
    istituto_vigilanza_id: r.istituto_vigilanza_id ?? "",
    cliente_id: r.cliente_id ?? "",
    sede_id: r.sede_id ?? "",
    sede_nome: "",
    assegnato_utente_id: r.assegnato_utente_id ?? "",
    regione: r.regione ?? "",
    provincia: r.provincia ?? "",
    indirizzo: r.indirizzo ?? "",
    ubicazione_negozio: r.ubicazione_negozio ?? "",
    note_ubicazione: r.note_ubicazione ?? "",
    tipo_locale: r.tipo_locale ?? "",
    numero_locali: r.numero_locali?.toString() ?? "",
    tipo_ingresso_uscita: r.tipo_ingresso_uscita ?? "",
    note_locali: r.note_locali ?? "",
    numero_accessi_locale: r.numero_accessi_locale ?? "",
    tipo_accesso_locale: r.tipo_accesso_locale ?? "",
    installazione_possibile: r.installazione_possibile ?? "",
    note_installazione: r.note_installazione ?? "",
    caratteristiche_pareti: r.caratteristiche_pareti ?? "",
    note_pareti: r.note_pareti ?? "",
    caratteristiche_pavimento: r.caratteristiche_pavimento ?? "",
    accessi_rischio_finestre: r.accessi_rischio?.finestre ?? "",
    accessi_rischio_lucernai: r.accessi_rischio?.lucernai ?? "",
    accessi_rischio_vetrine: r.accessi_rischio?.vetrine ?? "",
    note_accessi_rischio: r.note_accessi_rischio ?? "",
    protezioni_finestre: r.protezioni_strutturali?.finestre ?? "",
    protezioni_lucernai: r.protezioni_strutturali?.lucernai ?? "",
    protezioni_vetrine: r.protezioni_strutturali?.vetrine ?? "",
    note_protezioni: r.note_protezioni ?? "",
    prese_schuko: r.prese_elettriche?.schuko ?? false,
    prese_tradizionale: r.prese_elettriche?.tradizionale ?? false,
    prese_punto_rete: r.prese_elettriche?.punto_rete ?? false,
    numero_prese: r.numero_prese ?? "",
    collegamento_internet: r.collegamento_internet ?? "",
    note_elettrico_rete: r.note_elettrico_rete ?? "",
    allarme_presente: b2s(r.allarme_presente),
    allarme_marca: r.allarme_marca ?? "",
    allarme_modello: r.allarme_modello ?? "",
    allarme_ultima_manutenzione: r.allarme_ultima_manutenzione?.slice(0, 10) ?? "",
    allarme_contratto_manutenzione: r.allarme_contratto_manutenzione ?? "",
    sensori_locale_cassaforte: r.sensori_locale_cassaforte ?? "",
    altri_sistemi_sicurezza: b2s(r.altri_sistemi_sicurezza),
    collegamento_vigilanza: b2s(r.collegamento_vigilanza),
    istituto_vigilanza_nome: r.istituto_vigilanza_nome ?? "",
    collegamento_forze_ordine: b2s(r.collegamento_forze_ordine),
    note_antintrusione: r.note_antintrusione ?? "",
    posizione_ostacoli: b2s(r.posizione_ostacoli),
    note_ostacoli: r.note_ostacoli ?? "",
    facile_accesso_installatore: b2s(r.facile_accesso_installatore),
    facile_accesso_vigilanza: b2s(r.facile_accesso_vigilanza),
    confini_posizione: r.confini_posizione ?? "",
    accesso_locali: r.accesso_locali ?? "",
    modello_da_installare: r.modello_da_installare ?? "",
    dispositivo_catalogo_id: r.dispositivo_catalogo_id ?? "",
    validita_installazione: r.validita_installazione?.toString() ?? "",
    esito_ispezione_locali: b2s(r.esito_ispezione_locali),
    esito_locale_cassaforte: b2s(r.esito_locale_cassaforte),
    esito_impianto_elettrico: b2s(r.esito_impianto_elettrico),
    esito_impianto_antintrusione: b2s(r.esito_impianto_antintrusione),
    esito_posizione_cassaforte: b2s(r.esito_posizione_cassaforte),
    disegno_locali_effettuato: b2s(r.disegno_locali_effettuato),
    stato: r.stato,
    note: r.note ?? "",
    ...riferimentiOdooDaRecord(r),
  };
}

// --- Piccoli helper per ridurre la ripetizione dei ~25 select del form -----
function Sel({
  register,
  name,
  label,
  options,
}: {
  register: UseFormRegister<FormValues>;
  name: keyof FormValues;
  label: string;
  options: string[];
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-ink">{label}</label>
      <select className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink" {...register(name)}>
        <option value="">—</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function YN({
  register,
  name,
  label,
  yesLabel = "Sì",
  noLabel = "No",
}: {
  register: UseFormRegister<FormValues>;
  name: keyof FormValues;
  label: string;
  yesLabel?: string;
  noLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-ink">{label}</label>
      <select className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink" {...register(name)}>
        <option value="">—</option>
        <option value="true">{yesLabel}</option>
        <option value="false">{noLabel}</option>
      </select>
    </div>
  );
}

function SectionTitle({ children }: { children: string }) {
  return <p className="mb-3 mt-2 text-sm font-bold uppercase tracking-wide text-ink/50">{children}</p>;
}

function TextArea({
  label,
  register,
  name,
}: {
  label: string;
  register: UseFormRegister<FormValues>;
  name: keyof FormValues;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-ink">{label}</label>
      <textarea
        rows={2}
        className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
        {...register(name)}
      />
    </div>
  );
}

/**
 * Sezione "Rendi disponibile ai Centri Assistenza" — SOLO admin, come da
 * richiesta esplicita: la condivisione è un'azione di accesso/sistema, non
 * operativa, quindi non è aperta a staff come il resto del modulo.
 */
function CondivisioneSection({ sopralluogoId }: { sopralluogoId: string }) {
  const { showToast } = useToast();
  const [condivisioni, setCondivisioni] = useState<CondivisioneSopralluogo[]>([]);
  const [tuttiCentri, setTuttiCentri] = useState<CentroAssistenzaOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCentroId, setSelectedCentroId] = useState("");
  const [isSharing, setIsSharing] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  async function load() {
    setIsLoading(true);
    try {
      const [condResult, centriResult] = await Promise.all([
        sopralluoghiApi.listCondivisioni(sopralluogoId),
        anagraficaApi.listCentriAssistenza(),
      ]);
      setCondivisioni(condResult.condivisioni);
      setTuttiCentri(centriResult.centri);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare le condivisioni."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sopralluogoId]);

  const centriNonCondivisi = tuttiCentri.filter((c) => !condivisioni.some((cond) => cond.centro_assistenza_id === c.id));

  async function handleShare() {
    if (!selectedCentroId) return;
    setIsSharing(true);
    try {
      await sopralluoghiApi.condividiSopralluogo(sopralluogoId, [selectedCentroId]);
      showToast("Sopralluogo reso disponibile al centro selezionato.", "success");
      setSelectedCentroId("");
      load();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile condividere il sopralluogo."), "error");
    } finally {
      setIsSharing(false);
    }
  }

  async function handleRevoke(centroId: string) {
    setRevokingId(centroId);
    try {
      await sopralluoghiApi.revocaCondivisione(sopralluogoId, centroId);
      showToast("Condivisione revocata.", "success");
      load();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile revocare la condivisione."), "error");
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <div>
      {isLoading ? (
        <p className="text-xs text-ink/40">Caricamento…</p>
      ) : (
        <>
          {condivisioni.length === 0 ? (
            <p className="mb-3 text-xs text-ink/40">
              Non ancora reso disponibile a nessun Centro Assistenza — lo vede solo chi l'ha compilato.
            </p>
          ) : (
            <div className="mb-3 flex flex-wrap gap-2">
              {condivisioni.map((c) => (
                <span
                  key={c.id}
                  className="flex items-center gap-1.5 rounded-full bg-primary/10 py-1 pl-3 pr-1.5 text-xs font-medium text-primary"
                >
                  {c.centro_assistenza.nome}
                  <button
                    type="button"
                    onClick={() => handleRevoke(c.centro_assistenza_id)}
                    disabled={revokingId === c.centro_assistenza_id}
                    aria-label={`Revoca condivisione con ${c.centro_assistenza.nome}`}
                    className="rounded-full p-0.5 hover:bg-primary/20 disabled:opacity-40"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {centriNonCondivisi.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <select
                value={selectedCentroId}
                onChange={(e) => setSelectedCentroId(e.target.value)}
                className="h-9 flex-1 rounded-lg border border-line bg-white px-2 text-sm text-ink"
              >
                <option value="">Scegli un Centro Assistenza…</option>
                {centriNonCondivisi.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
              <Button size="sm" variant="secondary" loading={isSharing} disabled={!selectedCentroId} onClick={handleShare}>
                <Share2 className="h-3.5 w-3.5" aria-hidden="true" />
                Rendi disponibile
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Sopralluoghi() {
  const { user, hasPermission } = useAuth();
  /** Pannello di invio per email del PDF di questo sopralluogo. */
  const [invioAperto, setInvioAperto] = useState(false);
  const { showToast } = useToast();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ritardo = searchParams.get("ritardo") === "true";
  // Mese, ordinamento e sopralluogo aperto stanno nell'URL come `ritardo`
  // (vedi `hooks/useStatoUrl.ts`): tornando indietro, o aprendo un link, si
  // ritrova la stessa vista. Il passo del modulo no: riaprendo un sopralluogo
  // si riparte dal primo, come prima.
  const scrivi = useScriviParametri();
  const { ordine, onOrdineChange } = useOrdinamentoUrl();

  const [records, setRecords] = useState<Sopralluogo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const mese = meseDaUrl(searchParams);
  const setMese = (m: number | undefined) => scrivi({ mese: m ? String(m) : null });
  const messaggioVuoto = useMessaggioListaVuota(
    "Nessun sopralluogo registrato.",
    Boolean(ritardo || mese !== undefined)
  );
  const [editingRecord, setEditingRecord] = useState<Sopralluogo | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [centri, setCentri] = useState<CentroAssistenzaOption[]>([]);
  const [istituti, setIstituti] = useState<IstitutoVigilanzaOption[]>([]);

  const isAdminOrStaff = user?.role === "admin" || user?.role === "staff";
  const puoGestireOrdini = usaPuoGestireRiferimentiOdoo();
  // Il cliente compila i sopralluoghi assegnati: per lui il nome del
  // compilatore è obbligatorio, perché il suo account può essere una casella
  // aziendale condivisa e non identificare una persona.
  const isClienteCompilatore = user?.role === "cliente";

  async function loadRecords() {
    setIsLoading(true);
    try {
      // mese/anno e ritardo filtrati lato server.
      const { records: list } = await sopralluoghiApi.listSopralluoghi({ mese, ritardo: ritardo || undefined });
      setRecords(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare i sopralluoghi."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mese, ritardo]);

  useEffect(() => {
    // Solo admin/staff possono precompilare un sopralluogo e assegnarlo a un
    // Centro Assistenza specifico — solo per loro serve la lista completa.
    if (isAdminOrStaff) {
      anagraficaApi.listCentriAssistenza().then(({ centri }) => setCentri(centri));
      anagraficaApi.listIstitutiVigilanza().then(({ istituti }) => setIstituti(istituti));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminOrStaff]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({ defaultValues: emptyFormValues() });

  // L'elenco degli assegnabili lo chiede AssegnatarioSelect, che serve tutti e
  // quattro i moduli e conosce anche gli operatori, non solo gli utenti
  // cliente. La vecchia `GET /sopralluoghi/assegnatari-cliente` è stata
  // rimossa: non aveva più chiamanti.
  const watchedClienteId = watch("cliente_id");

  // Handoff (Fase 3.5): stato del select centro per un sopralluogo completato dal cliente
  const [handoffCentroId, setHandoffCentroId] = useState("");
  const [isAssegnandoCentro, setIsAssegnandoCentro] = useState(false);

  async function handleAssegnaCentro() {
    if (!editingRecord || !handoffCentroId) return;
    setIsAssegnandoCentro(true);
    try {
      await sopralluoghiApi.assegnaCentro(editingRecord.id, { centro_assistenza_id: handoffCentroId });
      showToast("Centro Assistenza assegnato. Ora puoi creare l'installazione collegata.", "success");
      pannello.chiudi();
      loadRecords();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile assegnare il Centro Assistenza."), "error");
    } finally {
      setIsAssegnandoCentro(false);
    }
  }

  function openCreate() {
    setEditingRecord(null);
    setCurrentStep(0);
    reset(emptyFormValues());
  }

  function openEdit(record: Sopralluogo) {
    setEditingRecord(record);
    setCurrentStep(0);
    setHandoffCentroId("");
    reset(toFormValues(record));
  }

  // Pannello su `?open=<id>` (clic su una riga, Calendario unificato, scheda
  // cliente) e su `?new=1` (pulsante, azione rapida della Dashboard).
  const pannello = usePannelloRecord({
    records,
    isLoading,
    preparaNuovo: openCreate,
    preparaModifica: openEdit,
  });
  const panelOpen = pannello.aperto;

  async function handleDelete() {
    if (!editingRecord) return;
    const confirmed = window.confirm(
      `Eliminare il sopralluogo di "${editingRecord.ragione_sociale_cliente ?? editingRecord.insegna_cliente ?? "questo cliente"}"? Verranno eliminate anche tutte le foto collegate. L'operazione non è reversibile.`
    );
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      await sopralluoghiApi.deleteSopralluogo(editingRecord.id);
      showToast("Sopralluogo eliminato.", "success");
      pannello.chiudi();
      loadRecords();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare il sopralluogo."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  // Download del PDF via apiClient (token allegato dall'interceptor), stesso
  // meccanismo di ExportButton per gli Excel — l'endpoint è autenticato,
  // quindi un semplice window.open non basterebbe.
  async function handleDownloadPdf() {
    if (!editingRecord) return;
    setIsDownloadingPdf(true);
    try {
      const response = await apiClient.get(`/sopralluoghi/${editingRecord.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const filename = `sopralluogo-${editingRecord.insegna_cliente ?? editingRecord.id}.pdf`.replace(
        /[^a-z0-9.\-]/gi,
        "_"
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile generare il PDF del sopralluogo."), "error");
    } finally {
      setIsDownloadingPdf(false);
    }
  }

  const onSubmit = handleSubmit(async (v) => {
    const payload: Partial<Sopralluogo> = {
      da_completare: v.da_completare,
      compilatore_nome: v.compilatore_nome || null,
      societa_appartenenza: v.societa_appartenenza || null,
      data_sopralluogo: v.data_sopralluogo || undefined,
      ragione_sociale_cliente: v.ragione_sociale_cliente || null,
      insegna_cliente: v.insegna_cliente || null,
      tipo_attivita: v.tipo_attivita || null,
      // Solo admin/staff vedono questo campo nel form (vedi passo 1) — per
      // chiunque altro resta "" e qui diventa undefined, così il backend
      // continua ad assegnare in automatico il proprio centro come sempre.
      centro_assistenza_id: isAdminOrStaff ? v.centro_assistenza_id || undefined : undefined,
      istituto_vigilanza_id: isAdminOrStaff ? v.istituto_vigilanza_id || undefined : undefined,
      // Assegnazione a utente cliente (Fase 3.4): solo admin/staff la impostano.
      // Il cliente compilatore non tocca questi campi (il backend li ignora).
      cliente_id: isAdminOrStaff ? v.cliente_id || undefined : undefined,
      sede_id: v.sede_id || null,
      assegnato_utente_id: isAdminOrStaff ? v.assegnato_utente_id || null : undefined,
      regione: v.regione || null,
      provincia: v.provincia || null,
      indirizzo: v.indirizzo || null,
      ubicazione_negozio: v.ubicazione_negozio || null,
      note_ubicazione: v.note_ubicazione || null,
      tipo_locale: v.tipo_locale || null,
      numero_locali: v.numero_locali ? Number(v.numero_locali) : null,
      tipo_ingresso_uscita: v.tipo_ingresso_uscita || null,
      note_locali: v.note_locali || null,
      numero_accessi_locale: v.numero_accessi_locale || null,
      tipo_accesso_locale: v.tipo_accesso_locale || null,
      installazione_possibile: v.installazione_possibile || null,
      note_installazione: v.note_installazione || null,
      caratteristiche_pareti: v.caratteristiche_pareti || null,
      note_pareti: v.note_pareti || null,
      caratteristiche_pavimento: v.caratteristiche_pavimento || null,
      accessi_rischio: {
        finestre: v.accessi_rischio_finestre || undefined,
        lucernai: v.accessi_rischio_lucernai || undefined,
        vetrine: v.accessi_rischio_vetrine || undefined,
      },
      note_accessi_rischio: v.note_accessi_rischio || null,
      protezioni_strutturali: {
        finestre: v.protezioni_finestre || undefined,
        lucernai: v.protezioni_lucernai || undefined,
        vetrine: v.protezioni_vetrine || undefined,
      },
      note_protezioni: v.note_protezioni || null,
      prese_elettriche: {
        schuko: v.prese_schuko,
        tradizionale: v.prese_tradizionale,
        punto_rete: v.prese_punto_rete,
      },
      numero_prese: v.numero_prese || null,
      collegamento_internet: v.collegamento_internet || null,
      note_elettrico_rete: v.note_elettrico_rete || null,
      allarme_presente: s2b(v.allarme_presente),
      allarme_marca: v.allarme_marca || null,
      allarme_modello: v.allarme_modello || null,
      allarme_ultima_manutenzione: v.allarme_ultima_manutenzione || null,
      allarme_contratto_manutenzione: v.allarme_contratto_manutenzione || null,
      sensori_locale_cassaforte: v.sensori_locale_cassaforte || null,
      altri_sistemi_sicurezza: s2b(v.altri_sistemi_sicurezza),
      collegamento_vigilanza: s2b(v.collegamento_vigilanza),
      istituto_vigilanza_nome: v.istituto_vigilanza_nome || null,
      collegamento_forze_ordine: s2b(v.collegamento_forze_ordine),
      note_antintrusione: v.note_antintrusione || null,
      posizione_ostacoli: s2b(v.posizione_ostacoli),
      note_ostacoli: v.note_ostacoli || null,
      facile_accesso_installatore: s2b(v.facile_accesso_installatore),
      facile_accesso_vigilanza: s2b(v.facile_accesso_vigilanza),
      confini_posizione: v.confini_posizione || null,
      accesso_locali: v.accesso_locali || null,
      modello_da_installare: v.modello_da_installare || null,
      dispositivo_catalogo_id: v.dispositivo_catalogo_id || null,
      validita_installazione: v.validita_installazione ? Number(v.validita_installazione) : null,
      esito_ispezione_locali: s2b(v.esito_ispezione_locali),
      esito_locale_cassaforte: s2b(v.esito_locale_cassaforte),
      esito_impianto_elettrico: s2b(v.esito_impianto_elettrico),
      esito_impianto_antintrusione: s2b(v.esito_impianto_antintrusione),
      esito_posizione_cassaforte: s2b(v.esito_posizione_cassaforte),
      disegno_locali_effettuato: s2b(v.disegno_locali_effettuato),
      stato: v.stato,
      note: v.note || null,
      // I cinque riferimenti OdA/OdV, tradotti dalla forma del form a quella del
      // database. Solo admin/staff: un sopralluogo lo compila anche un utente
      // CLIENTE (Fase 3.4), e il numero dell'ordine al fornitore non è cosa sua —
      // il backend li ignora comunque per chiunque non sia admin o staff.
      // Solo i CAMBIATI: fattura e garanzia sono di un ordine che può coprire
      // altre attività (vedi `payloadRiferimentiOdoo`).
      ...(puoGestireOrdini ? payloadRiferimentiOdoo(v, riferimentiOdooDaRecord(editingRecord)) : {}),
    };

    try {
      if (editingRecord) {
        // L'organizzazione non passa dalla PATCH (che la scarta di proposito):
        // se è cambiata va spostata con l'endpoint dedicato, e PRIMA — così
        // l'assegnatario viene validato contro il centro NUOVO. Al contrario
        // si otterrebbe un 400 su un nome che l'interfaccia aveva appena
        // offerto come valido.
        const centroCambiato = (v.centro_assistenza_id || null) !== (editingRecord.centro_assistenza_id || null);
        const istitutoCambiato = (v.istituto_vigilanza_id || null) !== (editingRecord.istituto_vigilanza_id || null);
        if (isAdminOrStaff && (centroCambiato || istitutoCambiato)) {
          await sopralluoghiApi.assegnaCentro(editingRecord.id, {
            centro_assistenza_id: v.centro_assistenza_id || null,
            istituto_vigilanza_id: v.istituto_vigilanza_id || null,
          });
        }
        await sopralluoghiApi.updateSopralluogo(editingRecord.id, payload);
        showToast("Sopralluogo aggiornato.", "success");
        pannello.chiudi();
        loadRecords();
      } else {
        const { record } = await sopralluoghiApi.createSopralluogo(payload);
        // Resta aperto sul record appena creato, per poter subito allegare le
        // foto: nell'URL `?new=1` diventa `?open=<id>`.
        setEditingRecord(record);
        pannello.passaAModifica(record.id);
        showToast("Sopralluogo creato. Ora puoi allegare le foto.", "success");
        loadRecords();
      }
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare il sopralluogo."), "error");
    }
  });

  const canCreate =
    user?.role !== "istituto_vigilanza" && user?.role !== "operatore_vigilanza" && user?.role !== "cliente";

  const columns: TableColumn<Sopralluogo>[] = [
    {
      key: "insegna_cliente",
      header: "Cliente",
      // Identifica la riga. Si ordina con lo stesso ripiego che si vede:
      // l'insegna quando c'è, altrimenti la ragione sociale — ordinare sulla
      // sola insegna manderebbe in fondo righe che a schermo un nome ce l'hanno.
      sempreVisibile: true,
      sortValue: (r) => r.insegna_cliente ?? r.ragione_sociale_cliente,
      render: (r) => r.insegna_cliente ?? r.ragione_sociale_cliente ?? "—",
    },
    {
      key: "indirizzo",
      header: "Indirizzo",
      sortValue: (r) => r.indirizzo,
      render: (r) => r.indirizzo ?? "—",
    },
    {
      key: "compilatore_nome",
      header: "Compilatore",
      sortValue: (r) => r.compilatore_nome,
      render: (r) => r.compilatore_nome ?? "—",
    },
    {
      key: "data_sopralluogo",
      header: "Data",
      // Non è nullable nello schema: nessun ramo per il vuoto, né qui né
      // nell'ordinamento.
      sortValue: (r) => r.data_sopralluogo,
      render: (r) => new Date(r.data_sopralluogo).toLocaleDateString("it-IT"),
    },
    {
      key: "stato",
      header: "Stato",
      // Porta "Da completare" (4 su 6 in produzione) e "In ritardo": è l'unico
      // avviso in tabella, quindi non si spegne. Si ordina sul solo stato
      // principale, come sugli altri moduli.
      sempreVisibile: true,
      sortValue: (r) => r.stato,
      render: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {/* «Non idoneo» non e' rosso: rosso vuol dire "qualcosa e' andato
              storto e va rifatto", ed e' quello che dice «Da rifare». Un posto
              non adatto e' una risposta completa e corretta — grigio neutro,
              come si segna una cosa chiusa e non un problema aperto. */}
          <Badge
            color={
              r.stato === "Approvato"
                ? "success"
                : r.stato === "Da rifare"
                  ? "danger"
                  : r.stato === "Non idoneo"
                    ? "neutral"
                    : "warning"
            }
          >
            {r.stato}
          </Badge>
          {r.da_completare && <Badge color="neutral">Da completare</Badge>}
          {r.in_ritardo && <Badge color="danger">In ritardo</Badge>}
        </div>
      ),
    },
    // Provincia e Tipo attività sono le due colonne che NON poggiano su un
    // riempimento misurato: in produzione i sopralluoghi sono 6, e su sei righe
    // una percentuale non decide niente. Le propone il significato — raggruppare
    // per zona e per tipo di esercizio — ed è per questo che nascono spente:
    // nel caso peggiore restano spente e non tolgono spazio a nessuno.
    {
      key: "provincia",
      header: "Provincia",
      nascostaDiDefault: true,
      sortValue: (r) => r.provincia,
      render: (r) => r.provincia ?? <span className="text-ink/30">—</span>,
    },
    {
      key: "tipo_attivita",
      header: "Tipo attività",
      nascostaDiDefault: true,
      sortValue: (r) => r.tipo_attivita,
      render: (r) => r.tipo_attivita ?? <span className="text-ink/30">—</span>,
    },
    {
      key: "organizzazione",
      header: "Organizzazione",
      // Il Centro Assistenza o l'Istituto di Vigilanza proprietario — uno dei
      // due, mai entrambi. Spenta di default: 4 su 6 in produzione, ma su sei
      // righe quel numero pesa poco, e la tabella ha già cinque colonne accese.
      nascostaDiDefault: true,
      sortValue: (r) => r.organizzazione ?? null,
      render: (r) => r.organizzazione ?? <span className="text-ink/30">—</span>,
    },
    {
      key: "assegnato",
      header: "Assegnato a",
      // Ora che la rotta risolve il nome (PR sull'include) l'intestazione può
      // prometterlo. Restano tre casi distinti, non due:
      //   nome presente          → si mostra il nome
      //   assegnato senza nome   → casella aziendale: "Assegnato" e basta,
      //                            che è quanto si sapeva prima dell'include
      //   non assegnato          → trattino
      // In produzione sono 3 su 6: resta spenta di default, e' una colonna da
      // approfondimento, non da colpo d'occhio.
      nascostaDiDefault: true,
      sortValue: (r) => r.assegnato_nome ?? (r.assegnato_utente_id ? "Assegnato" : null),
      render: (r) =>
        r.assegnato_nome ? (
          r.assegnato_nome
        ) : r.assegnato_utente_id ? (
          <Badge color="primary">Assegnato</Badge>
        ) : (
          <span className="text-ink/30">—</span>
        ),
    },
    {
      key: "note",
      header: "Note",
      nascostaDiDefault: true,
      sortValue: (r) => r.note,
      render: (r) =>
        r.note ? (
          <span className="line-clamp-2 text-xs text-ink/70">{r.note}</span>
        ) : (
          <span className="text-ink/30">—</span>
        ),
    },
  ];

  return (
    <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div
            style={sectionFlatStyle("sopralluoghi")}
            className="flex h-11 w-11 items-center justify-center rounded-xl"
          >
            <ClipboardCheck className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">Sopralluoghi</h1>
            <p className="mt-1 text-sm text-ink/50">Rilievo tecnico pre-installazione, con foto e quadro di sintesi.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MeseSelect value={mese} onChange={setMese} />
          <ExportButton
            endpoint="/sopralluoghi/export"
            filename="sopralluoghi.xlsx"
            fields={[
              { key: "stato", label: "Stato", type: "select", options: STATI },
              { key: "data_da", label: "Data sopralluogo da", type: "date" },
              { key: "data_a", label: "Data sopralluogo a", type: "date" },
            ]}
          />
          {canCreate && (
            <Button
              onClick={() => pannello.apriNuovo()}
              style={sectionTileStyle("sopralluoghi")}
              className="active:translate-y-0 active:shadow-none"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nuovo sopralluogo
            </Button>
          )}
        </div>
      </header>

      {ritardo && <RitardoBanner onRemove={() => scrivi({ ritardo: null }, { push: true })} />}

      <Table
        columns={columns}
        data={records}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        emptyMessage={messaggioVuoto}
        onRowClick={(r) => pannello.apri(r.id)}
        ordinabile
        ordine={ordine}
        onOrdineChange={onOrdineChange}
        sceltaColonne="sopralluoghi"
      />

      <SlideOver
        open={panelOpen}
        onClose={pannello.chiudi}
        title={editingRecord ? editingRecord.insegna_cliente ?? "Modifica sopralluogo" : "Nuovo sopralluogo"}
        size="xl"
        footer={
          <>
            {editingRecord && (
              <Button type="button" variant="ghost" loading={isDownloadingPdf} onClick={handleDownloadPdf}>
                <FileText className="h-4 w-4" aria-hidden="true" />
                Scarica PDF
              </Button>
            )}
            {/* Accanto a "Scarica", non al posto suo: sono due azioni diverse
                sullo stesso documento. Compare solo con `report:invia` — il
                permesso di spedire verso l'esterno, che non tutti hanno pur
                potendo leggere il sopralluogo. */}
            {editingRecord && hasPermission("report:invia") && (
              <Button type="button" variant="ghost" onClick={() => setInvioAperto(true)}>
                <Send className="h-4 w-4" aria-hidden="true" />
                Invia
              </Button>
            )}
            {editingRecord && (user?.role === "admin" || user?.role === "staff") && (
              <Button type="button" variant="ghost" loading={isDeleting} onClick={handleDelete}>
                Elimina
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={pannello.chiudi}>
              Chiudi
            </Button>
            <Button type="submit" form="sopralluogo-form" loading={isSubmitting}>
              Salva
            </Button>
          </>
        }
      >
        <form id="sopralluogo-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          {/* --- Pillole di navigazione tra i passi --- */}
          <div className="mb-2 flex flex-wrap gap-1.5">
            {STEPS.map((label, i) => (
              <button
                key={label}
                type="button"
                onClick={() => setCurrentStep(i)}
                className={clsx(
                  "rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
                  i === currentStep ? "bg-primary text-white" : "bg-black/5 text-ink/50 hover:bg-black/10"
                )}
              >
                {i + 1}. {label}
              </button>
            ))}
          </div>

          <div className={clsx("flex flex-col gap-4", currentStep !== 0 && "hidden")}>
            <label className="flex items-start gap-2 rounded-lg border border-line bg-ink/[0.02] px-3 py-2.5 text-sm text-ink">
              <input type="checkbox" className="mt-0.5 h-4 w-4" {...register("da_completare")} />
              <span>
                <span className="font-medium">Da completare</span>
                <span className="text-ink/50">
                  {" "}
                  — sopralluogo assegnato ma non ancora svolto sul campo. Imposta la "Data sopralluogo" come data
                  prevista: se passata e non completato, comparirà tra i ritardi.
                </span>
              </span>
            </label>
            {/* Obbligatorio per il cliente, come lato server: il suo account
                può essere una casella aziendale condivisa, e allora è questo
                campo — non l'account — a dire chi ha compilato. */}
            <Input
              label="Nome e cognome del compilatore"
              required={isClienteCompilatore}
              hint={isClienteCompilatore ? "Indica chi ha compilato materialmente il sopralluogo." : undefined}
              error={errors.compilatore_nome?.message}
              {...register("compilatore_nome", {
                required: isClienteCompilatore ? "Indica il nome di chi ha compilato il sopralluogo." : false,
              })}
            />
            <Sel register={register} name="societa_appartenenza" label="Società di appartenenza" options={SOCIETA} />
            <Input label="Data sopralluogo" type="date" {...register("data_sopralluogo")} />
            <ClienteAutocomplete
              label="Ragione sociale cliente"
              value={watch("ragione_sociale_cliente")}
              onChange={(v) => setValue("ragione_sociale_cliente", v)}
              onSelect={(c) => {
                setValue("cliente_id", c.id); // serve per assegnare a un utente cliente (Fase 3.4)
                setValue("assegnato_utente_id", ""); // cambio azienda -> resetta l'assegnatario
                if (!watch("insegna_cliente") && c.insegna) setValue("insegna_cliente", c.insegna);
                if (!watch("indirizzo") && c.indirizzo) setValue("indirizzo", c.indirizzo);
                if (!watch("provincia") && c.provincia) setValue("provincia", c.provincia);
              }}
            />
            <Input label="Insegna cliente" {...register("insegna_cliente")} />
            <SedeAutocomplete
              label="Sede (opzionale)"
              value={watch("sede_nome")}
              onChange={(v) => setValue("sede_nome", v)}
              sedeId={watch("sede_id")}
              onSedeIdChange={(v) => setValue("sede_id", v)}
              soloSelezione
              onSelect={(s) => {
                // Se il cliente non è ancora scelto, eredita l'azienda madre della sede.
                if (!watch("cliente_id") && s.cliente_id) setValue("cliente_id", s.cliente_id);

                // Il resto di ciò che la sede sa già: prima restava da
                // ridigitare al passo «Location», due passi più avanti e senza
                // più la sede sott'occhio — cioè nel punto in cui è più facile
                // scrivere una variante dello stesso indirizzo.
                //
                // Qui si sovrascrive anche quando il campo è pieno, al
                // contrario di ClienteAutocomplete qui sopra che riempie solo i
                // vuoti. La differenza non è di stile: il cliente è l'azienda e
                // il suo indirizzo è quello della sede legale, quindi un
                // ripiego che non deve cancellare un dato più preciso; la sede
                // È il posto di cui parla il sopralluogo. Sceglierne una e
                // vedersi restare l'indirizzo di un'altra sarebbe il difetto
                // opposto. Scatta solo sulla scelta esplicita dalla tendina,
                // mai aprendo un sopralluogo già salvato.
                if (s.indirizzo) setValue("indirizzo", s.indirizzo);
                const provincia = provinciaPerElenco(s.provincia);
                if (provincia) setValue("provincia", provincia);
                // La regione non è un campo della sede: si ricava dalla
                // provincia (vedi utils/province.ts). Se la sigla non è nota il
                // campo si lascia com'è invece di svuotarlo.
                const regione = regionePerProvincia(s.provincia);
                if (regione) setValue("regione", regione);
                // L'insegna la porta il nome della sede, come già fa
                // Installazioni con il proprio campo «Insegna».
                if (s.nome_sede) setValue("insegna_cliente", s.nome_sede);
              }}
            />
            <Sel register={register} name="tipo_attivita" label="Tipo attività" options={TIPI_ATTIVITA} />

            {isAdminOrStaff && (
              <div className="flex flex-col gap-1.5 rounded-lg border border-dashed border-accent/40 bg-accent/5 p-3">
                <label className="text-sm font-medium text-ink">Assegna a (opzionale)</label>
                <select
                  className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                  value={watch("centro_assistenza_id") ? `centro:${watch("centro_assistenza_id")}` : watch("istituto_vigilanza_id") ? `istituto:${watch("istituto_vigilanza_id")}` : ""}
                  onChange={(e) => {
                    const [tipo, id] = e.target.value.split(":");
                    setValue("centro_assistenza_id", tipo === "centro" ? id : "");
                    setValue("istituto_vigilanza_id", tipo === "istituto" ? id : "");
                    // L'assegnatario NON si azzera più cambiando centro o
                    // istituto: da quando esiste l'assegnazione a operatore,
                    // organizzazione e persona stanno insieme — il centro dice
                    // quale azienda è responsabile, la persona chi dentro
                    // quell'azienda. Ci pensa AssegnatarioSelect a svuotarlo
                    // se la persona scelta non appartiene al nuovo centro.
                  }}
                >
                  <option value="">— Nessuno (resta tuo) —</option>
                  <optgroup label="Centro Assistenza">
                    {centri.map((c) => (
                      <option key={c.id} value={`centro:${c.id}`}>
                        {c.nome}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Istituto di Vigilanza">
                    {istituti.map((i) => (
                      <option key={i.id} value={`istituto:${i.id}`}>
                        {i.nome}
                      </option>
                    ))}
                  </optgroup>
                </select>

                {/* Assegnazione a PERSONA. Prima era un select a sé che
                    elencava solo utenti `cliente` ed era ALTERNATIVO a
                    centro/istituto; ora è il campo condiviso dai quattro
                    moduli e convive con l'organizzazione: un operatore si
                    assegna insieme al suo centro, un cliente insieme alla sua
                    azienda. Chi è ammesso lo decide il backend. */}
                <div className="mt-2">
                  <AssegnatarioSelect
                    value={watch("assegnato_utente_id")}
                    onChange={(id) => setValue("assegnato_utente_id", id)}
                    cliente_id={watchedClienteId}
                    // Segue il centro SCELTO QUI, anche in modifica. Prima
                    // usava quello del record salvato, perché la PATCH scarta
                    // l'organizzazione e offrire gli operatori del centro
                    // appena selezionato avrebbe proposto nomi rifiutati con un
                    // 400. Ora il salvataggio sposta davvero il sopralluogo
                    // (chiamando /assegna-centro prima della PATCH), quindi il
                    // centro selezionato È quello contro cui verrà validato.
                    centro_assistenza_id={watch("centro_assistenza_id")}
                    istituto_vigilanza_id={watch("istituto_vigilanza_id")}
                    // Su un record esistente conta anche con CHI è condiviso:
                    // chi lo vede per condivisione può esserne responsabile.
                    // Le condivisioni le risolve il backend dall'id.
                    sopralluogo_id={editingRecord?.id}
                    aiuto={
                      editingRecord
                        ? "Chi se ne occupa: la sua organizzazione, o un centro con cui è condiviso."
                        : "Assegnandolo a un utente cliente, lo troverà fra i sopralluoghi da compilare."
                    }
                  />
                </div>
              </div>
            )}
          </div>

          <div className={clsx("flex flex-col gap-4", currentStep !== 1 && "hidden")}>
            <div className="grid grid-cols-2 gap-3">
              <Sel register={register} name="regione" label="Regione" options={REGIONI} />
              <Sel register={register} name="provincia" label="Provincia" options={PROVINCE} />
            </div>
            <Input label="Indirizzo" {...register("indirizzo")} />
            <Sel register={register} name="ubicazione_negozio" label="Ubicazione negozio" options={UBICAZIONE_OPZIONI} />
            <TextArea label="Note su ubicazione" register={register} name="note_ubicazione" />
          </div>

          <div className={clsx("flex flex-col gap-4", currentStep !== 2 && "hidden")}>
            <Sel
              register={register}
              name="tipo_locale"
              label="Tipo locale negozio"
              options={["ambiente unico", "più ambienti"]}
            />
            <Input label="Numero locali" type="number" {...register("numero_locali")} />
            <Sel register={register} name="tipo_ingresso_uscita" label="Ingresso/uscita negozio" options={INGRESSO_USCITA_OPZIONI} />
            <TextArea label="Note su locali" register={register} name="note_locali" />
          </div>

          <div className={clsx("flex flex-col gap-4", currentStep !== 3 && "hidden")}>
            <Sel register={register} name="numero_accessi_locale" label="Numero accessi al locale" options={NUMERO_ACCESSI_OPZIONI} />
            <Sel register={register} name="tipo_accesso_locale" label="Tipo accesso (porta)" options={TIPO_ACCESSO_LOCALE_OPZIONI} />
            <Sel
              register={register}
              name="installazione_possibile"
              label="Installazione possibile"
              options={["solo front-office", "back-office", "richiesta cliente su front-office"]}
            />
            <TextArea label="Note su installazione" register={register} name="note_installazione" />
            <Sel
              register={register}
              name="caratteristiche_pareti"
              label="Caratteristiche pareti"
              options={["pareti perimetrali in cartongesso", "pareti perimetrali in muratura", "pareti in altri materiali"]}
            />
            <TextArea label="Note su pareti" register={register} name="note_pareti" />
            <Sel
              register={register}
              name="caratteristiche_pavimento"
              label="Caratteristiche pavimento"
              options={["pavimento battuto", "pavimento flottante"]}
            />

            <p className="text-sm font-medium text-ink">Accessi a rischio</p>
            <div className="grid grid-cols-3 gap-3">
              <Sel register={register} name="accessi_rischio_finestre" label="Finestre" options={RISCHIO_OPZIONI} />
              <Sel register={register} name="accessi_rischio_lucernai" label="Lucernai" options={RISCHIO_OPZIONI} />
              <Sel register={register} name="accessi_rischio_vetrine" label="Vetrine" options={RISCHIO_OPZIONI} />
            </div>
            <TextArea label="Note su accessi a rischio" register={register} name="note_accessi_rischio" />

            <p className="text-sm font-medium text-ink">Protezioni strutturali</p>
            <div className="grid grid-cols-3 gap-3">
              <Sel register={register} name="protezioni_finestre" label="Finestre" options={PROTEZIONE_OPZIONI} />
              <Sel register={register} name="protezioni_lucernai" label="Lucernai" options={PROTEZIONE_OPZIONI} />
              <Sel register={register} name="protezioni_vetrine" label="Vetrine" options={PROTEZIONE_OPZIONI} />
            </div>
            <TextArea label="Note su finestre/lucernai/vetrine" register={register} name="note_protezioni" />
          </div>

          <div className={clsx("flex flex-col gap-4", currentStep !== 4 && "hidden")}>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" className="h-4 w-4 rounded border-black/20" {...register("prese_schuko")} />
                Presa Schuko 220V
              </label>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" className="h-4 w-4 rounded border-black/20" {...register("prese_tradizionale")} />
                Presa tradizionale 220V
              </label>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" className="h-4 w-4 rounded border-black/20" {...register("prese_punto_rete")} />
                Punto rete
              </label>
            </div>
            <Input label="Numero prese" {...register("numero_prese")} />
            <Sel
              register={register}
              name="collegamento_internet"
              label="Collegamento Internet"
              options={["ADSL", "FIBRA", "il cliente non lo sa", "altro", "non presente"]}
            />
            <TextArea label="Note su elettrico/rete" register={register} name="note_elettrico_rete" />
          </div>

          <div className={clsx("flex flex-col gap-4", currentStep !== 5 && "hidden")}>
            <YN register={register} name="allarme_presente" label="Impianto d'allarme presente" yesLabel="Presente" noLabel="Non presente" />
            <Input label="Marca centrale allarme" {...register("allarme_marca")} />
            <Input label="Modello centrale allarme" {...register("allarme_modello")} />
            <Input label="Ultima manutenzione" type="date" {...register("allarme_ultima_manutenzione")} />
            <Sel
              register={register}
              name="allarme_contratto_manutenzione"
              label="Contratto di manutenzione"
              options={["Annuale", "Semestrale", "A chiamata secondo necessità", "Nessun contratto"]}
            />
            <Sel
              register={register}
              name="sensori_locale_cassaforte"
              label="Sensori nel locale cassaforte"
              options={["SI", "No", "Il cliente non lo sa"]}
            />
            <YN register={register} name="altri_sistemi_sicurezza" label="Altri sistemi di sicurezza" />
            <YN register={register} name="collegamento_vigilanza" label="Collegamento alla vigilanza" />
            <Input label="Nome istituto di vigilanza" {...register("istituto_vigilanza_nome")} />
            <YN register={register} name="collegamento_forze_ordine" label="Collegamento alle Forze dell'Ordine" />
            <TextArea label="Note su allarme/sicurezza" register={register} name="note_antintrusione" />
          </div>

          <div className={clsx("flex flex-col gap-4", currentStep !== 6 && "hidden")}>
            <YN register={register} name="posizione_ostacoli" label="Presenza ostacoli" />
            <TextArea label="Note su ostacoli" register={register} name="note_ostacoli" />
            <YN register={register} name="facile_accesso_installatore" label="Facile accesso per l'installatore" />
            <YN register={register} name="facile_accesso_vigilanza" label="Facile accesso per la vigilanza" />
            <Input label="Confini della posizione" {...register("confini_posizione")} />
            <Sel
              register={register}
              name="accesso_locali"
              label="Chi ha accesso ai locali"
              options={["solo personale", "anche clienti"]}
            />
            <DispositivoAutocomplete
              label="Modello da installare"
              placeholder="Es. YOUGO UB2500SI"
              tipoDispositivo="Cassaforte"
              value={watch("modello_da_installare")}
              onChange={(v) => setValue("modello_da_installare", v)}
              onSelect={(d) => {
                setValue("modello_da_installare", `${d.marca} ${d.modello}`);
                setValue("dispositivo_catalogo_id", d.id);
              }}
            />
            {watch("dispositivo_catalogo_id") && <DispositivoMateriale dispositivoId={watch("dispositivo_catalogo_id")} />}
            <Input
              label="Validità installazione (0-10)"
              type="number"
              min={0}
              max={10}
              {...register("validita_installazione")}
            />
          </div>

          <div className={clsx("flex flex-col gap-4", currentStep !== 7 && "hidden")}>
            <YN register={register} name="esito_ispezione_locali" label="Ispezione locali conforme" yesLabel="Conforme" noLabel="Non conforme" />
            <YN register={register} name="esito_locale_cassaforte" label="Locale cassaforte conforme" yesLabel="Conforme" noLabel="Non conforme" />
            <YN register={register} name="esito_impianto_elettrico" label="Impianto elettrico/rete conforme" yesLabel="Conforme" noLabel="Non conforme" />
            <YN register={register} name="esito_impianto_antintrusione" label="Impianto antintrusione conforme" yesLabel="Conforme" noLabel="Non conforme" />
            <YN register={register} name="esito_posizione_cassaforte" label="Posizione cassaforte conforme" yesLabel="Conforme" noLabel="Non conforme" />
            <YN register={register} name="disegno_locali_effettuato" label="Disegno locali effettuato" />

            <Sel register={register} name="stato" label="Stato revisione" options={STATI} />
            <TextArea label="Note generali" register={register} name="note" />

            {/* Riferimenti OdA/OdV — qui e non al primo passo: non sono un dato
                del rilievo ma una trascrizione amministrativa che arriva dopo.
                Solo admin/staff, perché il passo 1 lo compila anche un cliente. */}
            {puoGestireOrdini && (
              <RiferimentiOdoo
                idPrefisso="sopralluogo"
                ordini={editingRecord?.ordini_odoo}
                valori={{
                  numero_oda: watch("numero_oda"),
                  oda_fattura_ricevuta: watch("oda_fattura_ricevuta"),
                  numero_odv: watch("numero_odv"),
                  odv_in_garanzia: watch("odv_in_garanzia"),
                  odv_fatturato: watch("odv_fatturato"),
                  spunte_toccate: watch("spunte_toccate"),
                }}
                onChange={(v) => {
                  setValue("numero_oda", v.numero_oda);
                  setValue("oda_fattura_ricevuta", v.oda_fattura_ricevuta);
                  setValue("numero_odv", v.numero_odv);
                  setValue("odv_in_garanzia", v.odv_in_garanzia);
                  setValue("odv_fatturato", v.odv_fatturato);
                  setValue("spunte_toccate", v.spunte_toccate);
                }}
              />
            )}
          </div>

          {/* --- Passo 9: Foto e piantina — fuori dal form React (non sono
              campi da inviare col submit, si caricano subito da sé) ma
              dentro il percorso visivo, così non serve scoprirlo scorrendo
              oltre il form. --- */}
          <div className={clsx(currentStep !== 8 && "hidden")}>
            {!editingRecord ? (
              <div className="rounded-lg border border-dashed border-black/20 bg-surface p-6 text-center text-sm text-ink/50">
                Premi <strong>"Salva"</strong> qui sotto per registrare il sopralluogo — solo dopo potrai caricare
                foto, video e la piantina.
              </div>
            ) : (
              <>
                <p className="mb-3 text-xs text-ink/40">
                  Su cellulare/tablet, "Scatta / carica foto" apre direttamente la fotocamera.
                </p>
                <CategorizedMediaUploader entityType="sopralluogo" entityId={editingRecord.id} categories={MEDIA_CATEGORIE} />
              </>
            )}
          </div>

          {/* --- Navigazione tra i passi --- */}
          <div className="mt-2 flex items-center justify-between border-t border-line pt-4">
            <Button
              type="button"
              variant="ghost"
              disabled={currentStep === 0}
              onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
            >
              ← Indietro
            </Button>
            <span className="text-xs text-ink/40">
              Passo {currentStep + 1} di {STEPS.length}
            </span>
            {currentStep < STEPS.length - 1 ? (
              <Button type="button" onClick={() => setCurrentStep((s) => Math.min(STEPS.length - 1, s + 1))}>
                Avanti →
              </Button>
            ) : (
              <span className="text-xs text-ink/40">Usa "Salva" qui sotto per completare.</span>
            )}
          </div>
        </form>

        {/* Handoff (Fase 3.5) — sopralluogo completato dal cliente, non ancora
            girato a un Centro Assistenza: admin/staff scelgono il centro. */}
        {editingRecord &&
          isAdminOrStaff &&
          editingRecord.compilato_da_cliente &&
          !editingRecord.centro_assistenza_id && (
            <div className="mt-6 rounded-lg border border-accent/40 bg-accent/5 p-4">
              <SectionTitle>Completato dal cliente — assegna a un Centro Assistenza</SectionTitle>
              <p className="mb-2 text-xs text-ink/50">
                Gira questo sopralluogo a un Centro Assistenza; potrai poi creare l'installazione collegata.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <select
                  value={handoffCentroId}
                  onChange={(e) => setHandoffCentroId(e.target.value)}
                  className="h-10 flex-1 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                >
                  <option value="">Seleziona un Centro Assistenza…</option>
                  {centri.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  onClick={handleAssegnaCentro}
                  loading={isAssegnandoCentro}
                  disabled={!handoffCentroId}
                >
                  Assegna centro
                </Button>
              </div>
            </div>
          )}

        {/* Sopralluogo già girato a un centro: crea l'installazione collegata (tracciabilità). */}
        {editingRecord &&
          isAdminOrStaff &&
          editingRecord.compilato_da_cliente &&
          editingRecord.centro_assistenza_id && (
            <div className="mt-6 rounded-lg border border-line p-4">
              <SectionTitle>Installazione collegata</SectionTitle>
              <p className="mb-2 text-xs text-ink/50">
                Sopralluogo assegnato a un Centro Assistenza. Crea l'installazione: risulterà collegata a questo
                sopralluogo.
              </p>
              <Button
                type="button"
                onClick={() =>
                  navigate(
                    `/schedule?new=1&sopralluogo=${editingRecord.id}` +
                      `&cliente_id=${editingRecord.cliente_id ?? ""}` +
                      `&cliente_nome=${encodeURIComponent(
                        editingRecord.ragione_sociale_cliente ?? editingRecord.insegna_cliente ?? ""
                      )}`
                  )
                }
              >
                Crea installazione collegata
              </Button>
            </div>
          )}

        {editingRecord && user?.role === "admin" && (
          <div className="mt-6 border-t border-line pt-4">
            <SectionTitle>Rendi disponibile ai Centri Assistenza</SectionTitle>
            <CondivisioneSection sopralluogoId={editingRecord.id} />
          </div>
        )}
      </SlideOver>

      {editingRecord && (
        <InviaReportModal
          open={invioAperto}
          onClose={() => setInvioAperto(false)}
          titolo="Invia il sopralluogo"
          nomeAllegato={`sopralluogo-${editingRecord.insegna_cliente ?? editingRecord.id}.pdf`}
          caricaDestinatari={() => sopralluoghiApi.destinatariPropostiSopralluogo(editingRecord.id)}
          invia={(destinatari, nota) => sopralluoghiApi.inviaSopralluogo(editingRecord.id, destinatari, nota)}
        />
      )}
    </div>
  );
}
